import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Compass, GraduationCap, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSES = [
  {
    code: 'qld_level2_upgrade',
    title: 'QLD Pilot — Level 2 Upgrade',
    badge: 'QLD',
    summary: 'Upgrade your QLD pilot accreditation from Level 1 to Level 2.'
  },
  {
    code: 'tlic3010_qld_level1',
    title: 'TLIC3010 Pilot or Escort Oversize / Overmass Loads (QLD Level 1)',
    badge: 'QLD · ASQA · TMR',
    summary: 'Level 1 pilot vehicle driver. Meets the standards set by the Australian Skills Quality Authority (ASQA) and the QLD Department of Transport and Main Roads (TMR).'
  },
  {
    code: 'tlif3060_vic_nt_wa',
    title: 'TLIF3060 Control Traffic as a Pilot Vehicle Operator',
    badge: 'VIC · NT · WA',
    summary: 'Covers VIC entry under QLD authority, refresher for TLIF3060 in VIC, and one of the required units for NT and WA control-traffic accreditation.'
  },
  {
    code: 'wa_hv_pilot_refresher',
    title: 'WA Heavy Vehicle Pilot Licence — Refresher',
    badge: 'WA · Refresher',
    summary: 'For pilots who already hold a current WA Heavy Vehicle Pilot accreditation and need to refresh.'
  },
  {
    code: 'hv_pilot_full_5units',
    title: 'Heavy Vehicle Pilot Training — Full 5-Unit Program (WA, VIC & NT)',
    badge: 'WA · VIC · NT',
    summary: 'Complete training across 5 units: TLIC3010, TLIF3013, TLIE3009, TLIF3060, TLIB0002.'
  },
  {
    code: 'vic_level2_refresher',
    title: 'Pilot Vehicle Operator Refresher (Level 2)',
    badge: 'VIC · Refresher',
    summary: 'Based on TLIF3060. For Level 2 certified pilot vehicle drivers renewing their accreditation in VIC.'
  }
];

const PilotAccreditation = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });
  const [selectedCourse, setSelectedCourse] = useState(null);

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!selectedCourse) {
      toast.error('Please select a course');
      return;
    }
    const required = ['name', 'phone', 'address', 'email'];
    const missing = required.filter(f => !form[f].trim());
    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(', ')}`);
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      toast.error('Please enter a valid email');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/pilot-accreditation-applications`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        email: form.email.trim(),
        course_code: selectedCourse.code,
        course_title: selectedCourse.title,
        notes: form.notes.trim() || null
      });
      setSubmitted(true);
      toast.success('Application sent — we\'ll be in touch shortly');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="pilot-accreditation"
      className="py-16 bg-gradient-to-b from-[#0f223a] to-[#1e3a5f] text-white"
      data-testid="pilot-accreditation-section"
    >
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-500/15 ring-1 ring-red-400/40 mb-4">
            <GraduationCap className="w-7 h-7 text-red-300" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 flex items-center justify-center gap-2">
            <Compass className="w-7 h-7 text-red-300" />
            Pilot Accreditation
          </h2>
          <p className="text-blue-100 max-w-2xl mx-auto">
            Become a certified pilot vehicle operator or refresh your existing accreditation.
            Select a course below, leave your details, and we&apos;ll get you onto the next intake.
          </p>
        </div>

        {/* Course cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {COURSES.map((c) => {
            const active = selectedCourse?.code === c.code;
            return (
              <button
                type="button"
                key={c.code}
                onClick={() => setSelectedCourse(c)}
                data-testid={`pilot-accred-course-${c.code}`}
                className={`text-left p-5 rounded-xl transition-all duration-200 border ring-1 ${
                  active
                    ? 'bg-white text-[#1e3a5f] border-red-400 ring-red-400 shadow-xl scale-[1.02]'
                    : 'bg-white/5 text-blue-50 border-white/10 ring-transparent hover:bg-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span
                    className={`text-[10px] tracking-wider font-bold uppercase px-2 py-1 rounded ${
                      active ? 'bg-red-500 text-white' : 'bg-red-500/20 text-red-200'
                    }`}
                  >
                    {c.badge}
                  </span>
                  {active && <CheckCircle2 className="w-5 h-5 text-red-500 flex-shrink-0" />}
                </div>
                <h3 className={`font-bold mb-2 leading-snug ${active ? 'text-[#1e3a5f]' : 'text-white'}`}>
                  {c.title}
                </h3>
                <p className={`text-sm leading-relaxed ${active ? 'text-gray-600' : 'text-blue-200'}`}>
                  {c.summary}
                </p>
              </button>
            );
          })}
        </div>

        {/* Application form */}
        <Card className="bg-white text-[#1e3a5f] p-6 sm:p-8 rounded-2xl shadow-2xl">
          {submitted ? (
            <div className="text-center py-10" data-testid="pilot-accred-success">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Application received</h3>
              <p className="text-gray-600">
                Thanks {form.name.split(' ')[0]} — we&apos;ve logged your interest for{' '}
                <span className="font-semibold">{selectedCourse?.title}</span>.
                Our team will contact you on <span className="font-semibold">{form.phone}</span> shortly.
              </p>
            </div>
          ) : (
            <>
              <h3 className="text-xl font-bold mb-1">Apply for this course</h3>
              <p className="text-sm text-gray-500 mb-5">
                {selectedCourse
                  ? <>Course selected: <span className="font-semibold text-[#1e3a5f]">{selectedCourse.title}</span></>
                  : <>Pick a course above, then fill in your details.</>}
              </p>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pa-name">Full Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="pa-name"
                      value={form.name}
                      onChange={set('name')}
                      placeholder="Jane Driver"
                      data-testid="pilot-accred-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pa-phone">Phone Number <span className="text-red-500">*</span></Label>
                    <Input
                      id="pa-phone"
                      type="tel"
                      value={form.phone}
                      onChange={set('phone')}
                      placeholder="0400 123 456"
                      data-testid="pilot-accred-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="pa-email">Email Address <span className="text-red-500">*</span></Label>
                  <Input
                    id="pa-email"
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    placeholder="jane@example.com"
                    data-testid="pilot-accred-email"
                  />
                </div>

                <div>
                  <Label htmlFor="pa-address">Address (including postcode) <span className="text-red-500">*</span></Label>
                  <Input
                    id="pa-address"
                    value={form.address}
                    onChange={set('address')}
                    placeholder="123 Main St, Brisbane QLD 4000"
                    data-testid="pilot-accred-address"
                  />
                </div>

                <div>
                  <Label htmlFor="pa-notes">Notes (optional)</Label>
                  <Input
                    id="pa-notes"
                    value={form.notes}
                    onChange={set('notes')}
                    placeholder="Any questions or context"
                    data-testid="pilot-accred-notes"
                  />
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !selectedCourse}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-base font-bold"
                  data-testid="pilot-accred-submit"
                >
                  {submitting ? 'Sending…' : 'Submit Application'}
                </Button>
                {!selectedCourse && (
                  <p className="text-xs text-center text-amber-600">
                    Select a course above to enable submission.
                  </p>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </section>
  );
};

export default PilotAccreditation;
